import { EventType, type GatewayEvent } from "./events";
import { arr, bool, int, num, obj, prettyJSON, str, type Json } from "./json";
import { mediaRefsInToolResult, type MediaRef } from "./media";
import { parseUsage, type TranscriptMessage, type UsageStats } from "./models";

// MARK: 对话流里的一项（对应 HermesKit 的 ChatItem）

export type NoticeLevel = "info" | "warning" | "error";

export interface ToolCall {
  toolID: string;
  name: string;
  /** 简短参数预览（例如命令行、文件名） */
  context?: string;
  argsText?: string;
  status: "running" | "done" | "failed";
  summary?: string;
  resultText?: string;
  inlineDiff?: string;
  duration?: number;
  /** 结果里的图片/视频 */
  media: MediaRef[];
}

export type TodoState = "pending" | "in_progress" | "completed" | "cancelled";

export interface TodoEntry { id: string; content: string; state: TodoState }

export interface SubagentRun {
  key: string;
  goal: string;
  status: string;
  model?: string;
  toolCount?: number;
  lastTool?: string;
  summary?: string;
  duration?: number;
}

export function subagentFinished(r: SubagentRun): boolean {
  return ["completed", "failed", "error", "timeout", "interrupted"].includes(r.status);
}

export type ChatKind =
  | { type: "user"; text: string }
  | { type: "assistant"; text: string; streaming: boolean }
  | { type: "reasoning"; text: string; streaming: boolean }
  | { type: "tool"; call: ToolCall }
  | { type: "todo"; todos: TodoEntry[] }
  | { type: "subagent"; run: SubagentRun }
  | { type: "sideResult"; kind: string; question?: string; text: string }
  | { type: "notice"; text: string; level: NoticeLevel };

export interface ChatItem {
  id: string;
  kind: ChatKind;
  /** 毫秒 */
  timestamp?: number;
  rowID?: number;
  /** 思考耗时（秒）：思考结束时写入，历史记录里没有这个信息 */
  duration?: number;
}

function parseTodo(raw: Json, index: number): TodoEntry | undefined {
  if (typeof raw === "string") return { id: String(index), content: raw, state: "pending" };
  const o = obj(raw);
  const content = str(o?.content) ?? str(o?.text) ?? str(o?.title);
  if (!o || !content) return undefined;
  const status = str(o.status) ?? "pending";
  const state: TodoState = ["pending", "in_progress", "completed", "cancelled"].includes(status) ? (status as TodoState) : "pending";
  return { id: str(o.id) ?? String(index), content, state };
}

export type TranscriptChange =
  | { type: "none" }
  | { type: "updated" }
  /** 一轮结束：最终文本 + 状态（complete / error / interrupted） */
  | { type: "turnCompleted"; text: string; status: string }
  | { type: "sideTaskCompleted"; kind: string; text: string }
  | { type: "toast"; text: string };

const NONE: TranscriptChange = { type: "none" };
const UPDATED: TranscriptChange = { type: "updated" };

export const rolePrefix = "[角色：";

/**
 * 把网关事件归约成对话列表的纯状态机（不做任何 IO，方便单测）。和 HermesKit 的 Transcript 行为一致。
 *
 * 一个回合的典型事件序列：
 * message.start → reasoning.delta* → message.delta* → message.interim → tool.start → tool.complete → message.delta* → message.complete
 *
 * 每次修改都会替换 items 数组和被改动的那一项（不原地改对象），React 可以按引用判断哪些行需要重绘。
 */
export class Transcript {
  items: ChatItem[] = [];
  isRunning = false;
  /** 临时状态行（“正在调用 terminal…”“压缩上下文中…”） */
  statusLine?: string;
  usage?: UsageStats;

  private openAssistantIndex?: number;
  private openReasoningIndex?: number;
  private turnStartIndex = 0;
  private sawReasoningDelta = false;
  private counter = 0;

  clone(): Transcript {
    const t = new Transcript();
    Object.assign(t, this);
    t.items = this.items;
    return t;
  }

  // MARK: 历史

  load(history: TranscriptMessage[], running = false): void {
    const items: ChatItem[] = [];
    // 同一份历史每次加载得到相同的 id：先显示本机缓存、再换成服务器版本时，列表认得出是同一批行
    this.counter = 0;
    this.openAssistantIndex = undefined;
    this.openReasoningIndex = undefined;
    for (const m of history) {
      const ts = m.timestamp !== undefined ? m.timestamp * 1000 : undefined;
      switch (m.role) {
        case "user": {
          if (!m.text) continue;
          const label = systemInjectedLabel(m.text);
          if (label) items.push({ id: this.nextID(), kind: { type: "notice", text: label, level: "info" }, timestamp: ts });
          else items.push({ id: this.nextID(), kind: { type: "user", text: m.text }, timestamp: ts, rowID: m.row_id });
          break;
        }
        case "assistant":
          if (m.reasoning) items.push({ id: this.nextID(), kind: { type: "reasoning", text: m.reasoning, streaming: false }, timestamp: ts });
          if (m.text) items.push({ id: this.nextID(), kind: { type: "assistant", text: m.text, streaming: false }, timestamp: ts, rowID: m.row_id });
          break;
        case "tool": {
          const toolID = this.nextID();
          const call: ToolCall = {
            toolID, name: m.name ?? "tool", context: m.context,
            argsText: m.args !== undefined ? prettyJSON(m.args) : undefined, status: "done", media: [],
          };
          items.push({ id: `tool-${toolID}`, kind: { type: "tool", call }, timestamp: ts });
          break;
        }
        case "system":
          if (!m.text || m.display_kind === "hidden") continue;
          items.push({ id: this.nextID(), kind: { type: "notice", text: m.text, level: "info" }, timestamp: ts });
          break;
      }
    }
    this.items = items;
    this.isRunning = running;
    this.turnStartIndex = items.length;
  }

  /** 把当前对话转回历史消息的格式（load 能原样读回），用于本机缓存。临时内容（出错提示、待办、子任务）不保存。 */
  historySnapshot(): TranscriptMessage[] {
    const out: TranscriptMessage[] = [];
    for (const item of this.items) {
      const ts = item.timestamp !== undefined ? item.timestamp / 1000 : undefined;
      const k = item.kind;
      switch (k.type) {
        case "user": out.push({ role: "user", text: k.text, timestamp: ts, row_id: item.rowID }); break;
        case "assistant": out.push({ role: "assistant", text: k.text, timestamp: ts, row_id: item.rowID }); break;
        case "reasoning": out.push({ role: "assistant", timestamp: ts, reasoning: k.text }); break;
        case "tool": {
          let args: Json | undefined;
          try { args = k.call.argsText ? JSON.parse(k.call.argsText) : undefined; } catch { args = undefined; }
          out.push({ role: "tool", timestamp: ts, name: k.call.name, context: k.call.context, args });
          break;
        }
        case "notice": if (k.level === "info") out.push({ role: "system", text: k.text, timestamp: ts }); break;
      }
    }
    return out;
  }

  /** 本地显示一行标签代替用户消息（角色对话的开场提示词） */
  appendUserLabel(label: string): void {
    this.push({ id: this.nextID(), kind: { type: "notice", text: label, level: "info" }, timestamp: Date.now() });
    this.turnStartIndex = this.items.length;
  }

  /** 本地回显用户消息（网关不会把用户输入作为事件发回来） */
  appendUser(text: string): string {
    const id = this.nextID();
    this.push({ id, kind: { type: "user", text }, timestamp: Date.now() });
    this.turnStartIndex = this.items.length;
    return id;
  }

  /** 撤掉最后一条还没发出去的用户消息 */
  removePendingUser(id: string): void {
    const last = this.items[this.items.length - 1];
    if (!last || last.id !== id || last.kind.type !== "user") return;
    this.items = this.items.slice(0, -1);
    this.turnStartIndex = Math.min(this.turnStartIndex, this.items.length);
  }

  updateUser(id: string, text: string): void {
    const i = this.items.findLastIndex((x) => x.id === id);
    if (i < 0 || this.items[i].kind.type !== "user") return;
    this.set(i, { kind: { type: "user", text } });
  }

  appendNotice(text: string, level: NoticeLevel = "info"): void {
    this.push({ id: this.nextID(), kind: { type: "notice", text, level }, timestamp: Date.now() });
  }

  /** 已提交、等待网关开始流式输出（让界面立刻显示“思考中”） */
  markAwaitingReply(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.sawReasoningDelta = false;
    this.statusLine = "等待回复…";
  }

  setRunning(running: boolean): void {
    this.isRunning = running;
    if (!running) { this.statusLine = undefined; this.sealOpenSegments(); }
  }

  // MARK: 事件

  apply(event: GatewayEvent): TranscriptChange {
    const p = obj(event.payload) ?? {};
    switch (event.type) {
      case EventType.messageStart:
        this.beginTurnIfNeeded();
        return UPDATED;

      case EventType.messageDelta:
        this.beginTurnIfNeeded();
        this.appendAssistant(str(p.text) ?? "");
        return UPDATED;

      case EventType.reasoningDelta:
        this.beginTurnIfNeeded();
        this.sawReasoningDelta = true;
        this.appendReasoning(str(p.text) ?? "");
        return UPDATED;

      case EventType.thinkingDelta:
        // 旧版回调（CLI 转圈动画那一路）；已有 reasoning.delta 时忽略，避免重复
        if (this.sawReasoningDelta) return NONE;
        this.beginTurnIfNeeded();
        this.appendReasoning(stripSpinner(str(p.text) ?? ""));
        return UPDATED;

      case EventType.reasoningAvailable: {
        const text = str(p.text);
        if (!text || this.turnHasReasoning()) return NONE;
        this.insert(this.openAssistantIndex ?? this.items.length,
          { id: this.nextID(), kind: { type: "reasoning", text, streaming: false }, timestamp: Date.now() });
        if (this.openAssistantIndex !== undefined) this.openAssistantIndex += 1;
        return UPDATED;
      }

      case EventType.messageInterim: {
        const text = str(p.text) ?? "";
        if (bool(p.already_streamed) === true) {
          this.sealAssistant();
        } else if (text) {
          this.sealAssistant();
          this.push({ id: this.nextID(), kind: { type: "assistant", text, streaming: false }, timestamp: Date.now() });
        }
        return UPDATED;
      }

      case EventType.toolGenerating: {
        const name = str(p.name);
        if (name) this.statusLine = `正在准备 ${name}…`;
        return UPDATED;
      }

      case EventType.toolStart: {
        this.beginTurnIfNeeded();
        this.sealOpenSegments();
        const toolID = str(p.tool_id) ?? this.nextID();
        const name = str(p.name) ?? "tool";
        this.upsertTool({
          toolID, name,
          context: str(p.context) ?? str(p.preview),
          argsText: str(p.args_text) ?? (p.args !== undefined ? prettyJSON(p.args) : undefined),
          status: "running", media: [],
        });
        this.statusLine = `正在调用 ${name}…`;
        return UPDATED;
      }

      case EventType.toolComplete: {
        const toolID = str(p.tool_id) ?? "";
        const result = obj(p.result);
        const failed = bool(result?.success) === false || (result?.error !== undefined && result.error !== null);
        const idx = this.items.findIndex((x) => x.id === `tool-${toolID}`);
        const existing = idx >= 0 ? this.items[idx].kind : undefined;
        if (existing?.type === "tool") {
          const call: ToolCall = {
            ...existing.call,
            status: failed ? "failed" : "done",
            summary: str(p.summary) ?? existing.call.summary,
            resultText: str(p.result_text) ?? existing.call.resultText,
            inlineDiff: str(p.inline_diff) ?? existing.call.inlineDiff,
            duration: num(p.duration_s) ?? existing.call.duration,
            media: failed ? existing.call.media : mediaRefsInToolResult(p.result),
          };
          this.set(idx, { kind: { type: "tool", call } });
        } else {
          this.upsertTool({
            toolID: toolID || this.nextID(), name: str(p.name) ?? "tool", status: failed ? "failed" : "done",
            summary: str(p.summary), resultText: str(p.result_text), duration: num(p.duration_s),
            media: failed ? [] : mediaRefsInToolResult(p.result),
          });
        }
        const todos = arr(p.todos);
        if (todos) this.upsertTodos(todos);
        this.statusLine = undefined;
        return UPDATED;
      }

      case EventType.todoUpdated:
        this.upsertTodos(arr(p.todos) ?? []);
        return UPDATED;

      case EventType.statusUpdate: {
        const text = str(p.text) ?? "";
        this.statusLine = text || undefined;
        return UPDATED;
      }

      case EventType.sessionUsage: {
        const u = parseUsage(p.usage);
        if (u) this.usage = u;
        return UPDATED;
      }

      case EventType.messageComplete:
        return this.completeTurn(p);

      case EventType.error: {
        const message = str(p.message) ?? "发生错误";
        this.finishTurn();
        this.appendNotice(message, "error");
        return { type: "toast", text: message };
      }

      case EventType.subagentSpawnRequested: case EventType.subagentStart: case EventType.subagentProgress:
      case EventType.subagentTool: case EventType.subagentComplete:
        this.upsertSubagent(event);
        return UPDATED;

      case EventType.backgroundComplete: case EventType.btwComplete: {
        const kind = event.type === EventType.btwComplete ? "btw" : "background";
        const text = str(p.text) ?? "";
        this.push({ id: this.nextID(), kind: { type: "sideResult", kind, question: str(p.question), text }, timestamp: Date.now() });
        return { type: "sideTaskCompleted", kind, text };
      }

      case EventType.reviewSummary: {
        const text = str(p.text);
        if (text) this.appendNotice(text, "info");
        return UPDATED;
      }

      case EventType.toolOutputRisk: {
        const name = str(p.name) ?? "工具";
        this.appendNotice(`${name} 的输出被标记为有风险（${str(p.risk) ?? ""}），已按策略处理`, "warning");
        return UPDATED;
      }

      case EventType.notice: {
        const message = str(p.message);
        return message ? { type: "toast", text: message } : NONE;
      }

      default:
        return NONE;
    }
  }

  // MARK: 内部

  private push(item: ChatItem): void {
    this.items = [...this.items, item];
  }

  private insert(index: number, item: ChatItem): void {
    const items = [...this.items];
    items.splice(index, 0, item);
    this.items = items;
  }

  private set(index: number, patch: Partial<ChatItem>): void {
    const items = [...this.items];
    items[index] = { ...items[index], ...patch };
    this.items = items;
  }

  private turnHasReasoning(): boolean {
    return this.items.slice(Math.min(this.turnStartIndex, this.items.length)).some((x) => x.kind.type === "reasoning");
  }

  private nextID(): string {
    this.counter += 1;
    return `i${this.counter}`;
  }

  private beginTurnIfNeeded(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.sawReasoningDelta = false;
    this.statusLine = undefined;
    if (this.turnStartIndex > this.items.length) this.turnStartIndex = this.items.length;
  }

  private appendAssistant(chunk: string): void {
    this.closeReasoning();
    const i = this.openAssistantIndex;
    const cur = i !== undefined && i < this.items.length ? this.items[i].kind : undefined;
    if (i !== undefined && cur?.type === "assistant") {
      this.set(i, { kind: { type: "assistant", text: cur.text + chunk, streaming: true } });
    } else {
      if (!chunk) return;
      this.push({ id: this.nextID(), kind: { type: "assistant", text: chunk, streaming: true }, timestamp: Date.now() });
      this.openAssistantIndex = this.items.length - 1;
    }
    this.statusLine = undefined;
  }

  private appendReasoning(chunk: string): void {
    if (!chunk) return;
    const i = this.openReasoningIndex;
    const cur = i !== undefined && i < this.items.length ? this.items[i].kind : undefined;
    const lastIndex = this.items.length - 1;
    const last = this.items[lastIndex]?.kind;
    if (i !== undefined && cur?.type === "reasoning") {
      this.set(i, { kind: { type: "reasoning", text: cur.text + chunk, streaming: true } });
    } else if (lastIndex >= this.turnStartIndex && last?.type === "reasoning") {
      // 紧挨着的上一段思考（中间没有工具或回答）：续写同一块
      this.set(lastIndex, { kind: { type: "reasoning", text: last.text + "\n\n" + chunk, streaming: true } });
      this.openReasoningIndex = lastIndex;
    } else {
      this.push({ id: this.nextID(), kind: { type: "reasoning", text: chunk, streaming: true }, timestamp: Date.now() });
      this.openReasoningIndex = this.items.length - 1;
    }
    this.statusLine = "思考中…";
  }

  private closeReasoning(): void {
    const i = this.openReasoningIndex;
    if (i !== undefined && i < this.items.length) {
      const item = this.items[i];
      if (item.kind.type === "reasoning") {
        this.set(i, {
          kind: { type: "reasoning", text: item.kind.text, streaming: false },
          duration: item.timestamp !== undefined ? (Date.now() - item.timestamp) / 1000 : item.duration,
        });
      }
    }
    this.openReasoningIndex = undefined;
  }

  private sealAssistant(): void {
    const i = this.openAssistantIndex;
    if (i !== undefined && i < this.items.length) {
      const k = this.items[i].kind;
      if (k.type === "assistant" && k.streaming) this.set(i, { kind: { type: "assistant", text: k.text, streaming: false } });
    }
    this.openAssistantIndex = undefined;
  }

  private sealOpenSegments(): void {
    this.closeReasoning();
    this.sealAssistant();
  }

  private upsertTool(call: ToolCall): void {
    const id = `tool-${call.toolID}`;
    const idx = this.items.findIndex((x) => x.id === id);
    if (idx >= 0) this.set(idx, { kind: { type: "tool", call } });
    else this.push({ id, kind: { type: "tool", call }, timestamp: Date.now() });
  }

  private upsertTodos(raw: Json[]): void {
    const todos = raw.map((r, i) => parseTodo(r, i)).filter((t): t is TodoEntry => !!t);
    const start = Math.min(this.turnStartIndex, this.items.length);
    let idx = -1;
    for (let i = this.items.length - 1; i >= start; i--) if (this.items[i].kind.type === "todo") { idx = i; break; }
    if (idx >= 0) this.set(idx, { kind: { type: "todo", todos } });
    else if (todos.length) this.push({ id: this.nextID(), kind: { type: "todo", todos }, timestamp: Date.now() });
  }

  private upsertSubagent(event: GatewayEvent): void {
    const p = obj(event.payload) ?? {};
    const key = str(p.subagent_id) ?? `${str(p.delegation_id) ?? "d"}-${int(p.task_index) ?? 0}`;
    const itemID = `sub-${key}`;
    const idx = this.items.findIndex((x) => x.id === itemID);
    const existing = idx >= 0 ? this.items[idx].kind : undefined;
    const run: SubagentRun = existing?.type === "subagent"
      ? { ...existing.run }
      : { key, goal: str(p.goal) ?? "子任务", status: "queued" };
    const goal = str(p.goal);
    if (goal) run.goal = goal;
    if (event.type === EventType.subagentStart) run.status = "running";
    else if (event.type === EventType.subagentComplete) run.status = str(p.status) ?? "completed";
    else {
      const status = str(p.status);
      if (status) run.status = status;
      else if (run.status === "queued" && event.type !== EventType.subagentSpawnRequested) run.status = "running";
    }
    run.model = str(p.model) ?? run.model;
    run.toolCount = int(p.tool_count) ?? run.toolCount;
    run.lastTool = str(p.tool_name) ?? run.lastTool;
    run.summary = str(p.summary) ?? run.summary;
    run.duration = num(p.duration_seconds) ?? run.duration;
    if (idx >= 0) this.set(idx, { kind: { type: "subagent", run } });
    else this.push({ id: itemID, kind: { type: "subagent", run }, timestamp: Date.now() });
  }

  private completeTurn(p: Record<string, Json>): TranscriptChange {
    const finalText = str(p.text) ?? "";
    const status = str(p.status) ?? "complete";

    // 有些 provider 的 reasoning.available 会把最终回答原样再发一遍，去掉这种重复的“思考过程”
    if (finalText) {
      const start = Math.min(this.turnStartIndex, this.items.length);
      const dup = this.items.findIndex((x, i) => i >= start && x.kind.type === "reasoning" && x.kind.text.trim() === finalText.trim());
      if (dup >= 0) {
        this.items = this.items.filter((_, i) => i !== dup);
        if (this.openAssistantIndex !== undefined && this.openAssistantIndex > dup) this.openAssistantIndex -= 1;
      }
    }

    const reasoning = str(p.reasoning);
    if (reasoning && reasoning !== finalText && !this.turnHasReasoning()) {
      this.insert(this.openAssistantIndex ?? this.items.length,
        { id: this.nextID(), kind: { type: "reasoning", text: reasoning, streaming: false }, timestamp: Date.now() });
      if (this.openAssistantIndex !== undefined) this.openAssistantIndex += 1;
    }

    if (status === "error") {
      this.sealOpenSegments();
      this.appendNotice(finalText || (str(p.warning) ?? "本轮执行出错"), "error");
    } else if (finalText) {
      // 以 complete 里的文本为准：流式内容缺失或不完整时补齐
      const i = this.openAssistantIndex;
      const cur = i !== undefined && i < this.items.length ? this.items[i].kind : undefined;
      if (i !== undefined && cur?.type === "assistant") {
        if (cur.text !== finalText) this.set(i, { kind: { type: "assistant", text: finalText, streaming: false } });
      } else if (this.lastAssistantTextInTurn() !== finalText) {
        this.push({ id: this.nextID(), kind: { type: "assistant", text: finalText, streaming: false }, timestamp: Date.now() });
      }
    }
    if (status === "interrupted") this.appendNotice("已停止", "info");
    const warning = str(p.warning);
    if (warning && status !== "error") this.appendNotice(warning, "warning");
    const u = parseUsage(p.usage);
    if (u) this.usage = u;
    this.finishTurn();
    return { type: "turnCompleted", text: finalText, status };
  }

  private lastAssistantTextInTurn(): string | undefined {
    const start = Math.min(this.turnStartIndex, this.items.length);
    for (let i = this.items.length - 1; i >= start; i--) {
      const k = this.items[i].kind;
      if (k.type === "assistant") return k.text;
    }
    return undefined;
  }

  private finishTurn(): void {
    this.sealOpenSegments();
    // 回合结束时仍在“运行中”的工具视为已结束（中断等情况）
    if (this.items.some((x) => x.kind.type === "tool" && x.kind.call.status === "running")) {
      this.items = this.items.map((x) => x.kind.type === "tool" && x.kind.call.status === "running"
        ? { ...x, kind: { type: "tool", call: { ...x.kind.call, status: "done" } } } : x);
    }
    this.isRunning = false;
    this.statusLine = undefined;
    this.turnStartIndex = this.items.length;
  }
}

/** 去掉 CLI 转圈动画的前缀，比如 "(｡•́︿•̀｡) formulating..."、"ಠ_ಠ reasoning…" */
export function stripSpinner(text: string): string {
  const verbs = "pondering|contemplating|musing|cogitating|ruminating|deliberating|mulling|reflecting|processing|reasoning|analyzing|computing|synthesizing|formulating|brainstorming";
  const re = new RegExp(`^[^\\p{Script=Han}\\n]{0,24}?\\b(?:${verbs})(?:\\.\\.\\.|…)\\s*`, "u");
  return text.replace(re, "");
}

/** Hermes 注入的“用户消息”（定时任务、技能调用的长提示、角色对话）在界面上显示成一行标签 */
export function systemInjectedLabel(text: string): string | undefined {
  if (text.startsWith(rolePrefix)) {
    const end = text.indexOf("]");
    if (end > 0) return "🎭 角色：" + text.slice(rolePrefix.length, end);
  }
  if (text.startsWith("[IMPORTANT: You are running as a scheduled cron job")) return "⏰ 定时任务自动运行";
  const skillPrefix = '[IMPORTANT: The user has invoked the "';
  if (text.startsWith(skillPrefix)) {
    const rest = text.slice(skillPrefix.length);
    const name = rest.slice(0, Math.max(0, rest.indexOf('"')));
    return name ? `⚡ 使用技能：${name}` : "⚡ 使用技能";
  }
  return undefined;
}

// MARK: 过程折叠（对应 HermesKit 的 TurnLayout）

export interface ProcessGroup {
  /** 第一项的 id：流式追加时保持不变，展开状态不会丢 */
  id: string;
  items: ChatItem[];
  /** 当前回合仍在进行（最后一组） */
  isLive: boolean;
}

export type TurnRow = { type: "item"; id: string; item: ChatItem } | { type: "process"; id: string; group: ProcessGroup };

export const ProcessInfo = {
  toolCount: (g: ProcessGroup) => g.items.filter((x) => x.kind.type === "tool").length,
  subagentCount: (g: ProcessGroup) => g.items.filter((x) => x.kind.type === "subagent").length,
  failedCount: (g: ProcessGroup) => g.items.filter((x) => x.kind.type === "tool" && x.kind.call.status === "failed").length,
  /** 思考总时长（历史记录里没有时为 undefined） */
  thinkingSeconds: (g: ProcessGroup): number | undefined => {
    const values = g.items.filter((x) => x.kind.type === "reasoning" && x.duration !== undefined).map((x) => x.duration!);
    return values.length ? values.reduce((a, b) => a + b, 0) : undefined;
  },
  hasReasoning: (g: ProcessGroup) => g.items.some((x) => x.kind.type === "reasoning"),
  /** 只有一段思考、没有别的步骤 */
  isReasoningOnly: (g: ProcessGroup) => g.items.length === 1 && g.items[0].kind.type === "reasoning",
  /** 不重复的工具名（按出现顺序） */
  toolNames: (g: ProcessGroup): string[] => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const x of g.items) if (x.kind.type === "tool" && !seen.has(x.kind.call.name)) { seen.add(x.kind.call.name); out.push(x.kind.call.name); }
    return out;
  },
  /** 工具产出的图片/视频（折叠时也要露出来） */
  media: (g: ProcessGroup): MediaRef[] => g.items.flatMap((x) => (x.kind.type === "tool" ? x.kind.call.media : [])),
};

/**
 * 把对话列表整理成“过程折叠”的显示行：一轮里的思考、工具调用、子任务、任务清单和中途的旁白，收进一个“处理过程”组；
 * 用户消息、最终回答、提示、旁路结果照常显示。
 */
export function turnRows(items: ChatItem[], isRunning: boolean): TurnRow[] {
  const turns: ChatItem[][] = [];
  let current: ChatItem[] = [];
  for (const item of items) {
    if (item.kind.type === "user") {
      if (current.length) turns.push(current);
      turns.push([item]);
      current = [];
    } else {
      current.push(item);
    }
  }
  if (current.length) turns.push(current);

  const rows: TurnRow[] = [];
  turns.forEach((turn, index) => {
    if (turn.length === 1 && turn[0].kind.type === "user") {
      rows.push({ type: "item", id: turn[0].id, item: turn[0] });
      return;
    }
    const live = isRunning && index === turns.length - 1;
    // 最后一段回答算“最终回答”，之前的回答是中途旁白，收进过程
    let finalAnswer = -1;
    for (let i = turn.length - 1; i >= 0; i--) if (turn[i].kind.type === "assistant") { finalAnswer = i; break; }
    let group: ChatItem[] = [];
    const flush = (isLive: boolean) => {
      if (!group.length) return;
      rows.push({ type: "process", id: `process-${group[0].id}`, group: { id: group[0].id, items: group, isLive } });
      group = [];
    };
    turn.forEach((item, i) => {
      switch (item.kind.type) {
        case "reasoning": case "tool": case "subagent": case "todo":
          group.push(item);
          break;
        case "assistant":
          if (i === finalAnswer) { flush(false); rows.push({ type: "item", id: item.id, item }); }
          else group.push(item);
          break;
        default:
          flush(false);
          rows.push({ type: "item", id: item.id, item });
      }
    });
    flush(live);
  });
  return rows;
}
